import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Link, useSearchParams } from "react-router-dom";
import {
  PaEmptyState,
  PaFilterBar,
  PaPageHeader,
  PaPagination,
  PaSearchSelect,
  PaStatusBadge,
} from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import { platformRoleAccess } from "helpers/RolesPermission";
import { getPaginationObj } from "Utils/utils";
import { fetchPlatformAuditLog, exportPlatformAuditLog } from "store/services/platformAdmin.services";
import { URL_PLATFORM_ADMIN_AUDIT_LOG } from "store/services/URL";
import PlatformAuditLogTable from "./PlatformAuditLogTable";
import {
  AUDIT_PICKER_MIN_SEARCH,
  COMMON_AUDIT_ACTIONS,
  parseAuditLogSearchParams,
  resolveAuditStaffSelection,
  resolveAuditTenantSelection,
  searchAuditStaff,
  searchAuditTenants,
  syncAuditLogSearchParams,
} from "./auditLogFilter.utils";
import {
  buildAuditLogExportQuery,
  buildAuditLogQuery,
  createDefaultAuditFilters,
  formatTenantLabel,
  formatUserLabel,
  validateDateRange,
} from "./auditLog.utils";

const formatTimestamp = (value) => {
  if (!value) {
    return "—";
  }

  const timestamp = moment(value);
  if (!timestamp.isValid()) {
    return "—";
  }

  return (
    <time dateTime={value} title={timestamp.format("D MMM YYYY, h:mm a")}>
      {timestamp.format("D MMM YYYY, h:mm a")}
    </time>
  );
};

const FilterField = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-gray-500">{label}</span>
    {children}
  </label>
);

const inputClassName =
  "block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus-visible:border-indigo-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500";

const PlatformAuditLog = () => {
  const canViewAudit = platformRoleAccess("platform.audit.view");
  const canExportAudit = platformRoleAccess("platform.audit.export");
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilters = useMemo(() => parseAuditLogSearchParams(searchParams), [searchParams]);

  const [exporting, setExporting] = useState(false);
  const [maxSearchDays, setMaxSearchDays] = useState(14);
  const [filters, setFilters] = useState(() => ({
    ...createDefaultAuditFilters(),
    spId: urlFilters.spId,
    adminUserId: urlFilters.adminUserId,
    action: urlFilters.action,
  }));
  const [tenantSelection, setTenantSelection] = useState(null);
  const [adminSelection, setAdminSelection] = useState(null);
  const [actionInput, setActionInput] = useState(urlFilters.action);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(getPaginationObj());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRangeError, setDateRangeError] = useState("");
  const [expandedRowId, setExpandedRowId] = useState(null);

  const baselineFilters = useMemo(
    () => createDefaultAuditFilters(maxSearchDays),
    [maxSearchDays]
  );

  const hasFiltersApplied = useMemo(
    () =>
      Boolean(
        actionInput ||
          filters.spId ||
          filters.adminUserId ||
          filters.action ||
          filters.from !== baselineFilters.from ||
          filters.to !== baselineFilters.to
      ),
    [actionInput, filters, baselineFilters]
  );

  useEffect(() => {
    let cancelled = false;

    const hydrateSelections = async () => {
      const [tenant, admin] = await Promise.all([
        urlFilters.spId ? resolveAuditTenantSelection(urlFilters.spId) : Promise.resolve(null),
        urlFilters.adminUserId
          ? resolveAuditStaffSelection(urlFilters.adminUserId)
          : Promise.resolve(null),
      ]);

      if (cancelled) {
        return;
      }

      setTenantSelection(tenant);
      setAdminSelection(admin);
    };

    hydrateSelections();

    return () => {
      cancelled = true;
    };
  }, [urlFilters.adminUserId, urlFilters.spId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => {
        if (prev.action === actionInput.trim()) {
          return prev;
        }

        return { ...prev, action: actionInput.trim(), page: 1 };
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [actionInput]);

  useEffect(() => {
    const next = syncAuditLogSearchParams(searchParams, filters, actionInput);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [actionInput, filters.adminUserId, filters.spId, filters.action, searchParams, setSearchParams]);

  const loadAuditLog = useCallback(async () => {
    if (!canViewAudit) {
      setLoading(false);
      return;
    }

    const rangeError = validateDateRange(filters.from, filters.to, maxSearchDays);
    if (rangeError) {
      setDateRangeError(rangeError);
      setLoading(false);
      return;
    }

    setDateRangeError("");
    setLoading(true);
    setError(null);

    try {
      const endpoint = buildAuditLogQuery(filters, URL_PLATFORM_ADMIN_AUDIT_LOG);
      const payload = await fetchPlatformAuditLog(endpoint);
      const auditLog = payload?.audit_log;

      setRows(Array.isArray(auditLog?.data) ? auditLog.data : []);
      setPagination(getPaginationObj(auditLog, filters));

      if (payload?.max_search_days) {
        setMaxSearchDays(payload.max_search_days);
      }
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, [canViewAudit, filters, maxSearchDays]);

  useEffect(() => {
    loadAuditLog();
  }, [loadAuditLog]);

  const handleClearFilters = () => {
    setActionInput("");
    setTenantSelection(null);
    setAdminSelection(null);
    setExpandedRowId(null);
    setFilters(createDefaultAuditFilters(maxSearchDays));
  };

  const handleToggleExpand = (rowId) => {
    setExpandedRowId((current) => (current === rowId ? null : rowId));
  };

  const handleTenantChange = useCallback((option) => {
    setTenantSelection(option);
    setFilters((prev) => ({
      ...prev,
      spId: option?.id ? String(option.id) : "",
      page: 1,
    }));
  }, []);

  const handleAdminChange = useCallback((option) => {
    setAdminSelection(option);
    setFilters((prev) => ({
      ...prev,
      adminUserId: option?.id ? String(option.id) : "",
      page: 1,
    }));
  }, []);

  const searchTenants = useCallback((term) => searchAuditTenants(term), []);
  const searchStaff = useCallback((term) => searchAuditStaff(term), []);

  const columns = useMemo(
    () => [
      {
        key: "created_at",
        label: "Timestamp",
        render: (row) => formatTimestamp(row.created_at),
      },
      {
        key: "admin",
        label: "Admin",
        render: (row) => (
          <span className="max-w-xs truncate" title={formatUserLabel(row.admin)}>
            {formatUserLabel(row.admin)}
          </span>
        ),
      },
      {
        key: "action",
        label: "Action",
        render: (row) => (
          <PaStatusBadge label={row.action || "—"} variant="neutral" />
        ),
      },
      {
        key: "tenant",
        label: "Tenant",
        render: (row) =>
          row.tenant?.sp_id ? (
            <Link
              to={`/tenants/${row.tenant.sp_id}`}
              className="text-indigo-600 hover:text-indigo-800"
              title={formatTenantLabel(row.tenant)}
            >
              {formatTenantLabel(row.tenant)}
            </Link>
          ) : (
            "—"
          ),
      },
      {
        key: "acting_user",
        label: "Target user",
        render: (row) => (
          <span className="max-w-xs truncate" title={formatUserLabel(row.acting_user)}>
            {formatUserLabel(row.acting_user)}
          </span>
        ),
      },
      {
        key: "reason",
        label: "Reason",
        cellClassName: "max-w-xs whitespace-normal",
        render: (row) => row.reason || "—",
      },
      {
        key: "metadata",
        label: "Metadata",
        headerClassName: "text-right",
        cellClassName: "text-right",
      },
    ],
    []
  );

  if (!canViewAudit) {
    return (
      <PlatformAdminLayout>
        <PaPageHeader title="Audit log" subtitle="Platform administration events" />
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <PaEmptyState
            title="Access restricted"
            message="You don't have access to this area"
            description="You need platform.audit.view to view the platform audit log."
          />
        </div>
      </PlatformAdminLayout>
    );
  }

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title="Audit log"
        subtitle={`Platform administration events · max ${maxSearchDays}-day range`}
        actions={
          canExportAudit ? (
            <button
              type="button"
              disabled={exporting || Boolean(dateRangeError)}
              onClick={async () => {
                setExporting(true);
                try {
                  await exportPlatformAuditLog(buildAuditLogExportQuery(filters));
                } catch (err) {
                  setError(err?.message || "Failed to export audit log");
                } finally {
                  setExporting(false);
                }
              }}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
          ) : null
        }
      />

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {dateRangeError ? (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {dateRangeError}
        </div>
      ) : null}

      <div className="mb-4 grid gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-4">
        <FilterField label="From">
          <input
            type="date"
            value={filters.from}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, from: event.target.value, page: 1 }))
            }
            className={inputClassName}
          />
        </FilterField>
        <FilterField label="To">
          <input
            type="date"
            value={filters.to}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, to: event.target.value, page: 1 }))
            }
            className={inputClassName}
          />
        </FilterField>
        <PaSearchSelect
          label="Tenant"
          value={tenantSelection}
          onChange={handleTenantChange}
          onSearch={searchTenants}
          minLength={AUDIT_PICKER_MIN_SEARCH}
          placeholder="Search tenant name, email, or ABN"
          hint="Search by name — no ID needed"
          emptyMessage="No tenants match"
        />
        <PaSearchSelect
          label="Admin"
          value={adminSelection}
          onChange={handleAdminChange}
          onSearch={searchStaff}
          minLength={AUDIT_PICKER_MIN_SEARCH}
          placeholder="Search platform staff name or email"
          hint="Platform staff who performed the action"
          emptyMessage="No staff match"
        />
      </div>

      <PaFilterBar
        className="mb-4"
        searchValue={actionInput}
        onSearchChange={setActionInput}
        searchPlaceholder="Filter by action (e.g. platform.tenant.suspend)"
        onClear={hasFiltersApplied ? handleClearFilters : undefined}
        resultCount={pagination.total}
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {COMMON_AUDIT_ACTIONS.map((action) => (
          <button
            key={action}
            type="button"
            onClick={() => {
              setActionInput(action);
              setFilters((prev) => ({ ...prev, action, page: 1 }));
            }}
            className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
              actionInput === action
                ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            {action.replace(/^platform\./, "").replace(/\./g, " · ")}
          </button>
        ))}
      </div>

      <PlatformAuditLogTable
        columns={columns}
        rows={rows}
        expandedRowId={expandedRowId}
        onToggleExpand={handleToggleExpand}
        isLoading={loading}
      />

      <PaPagination
        page={filters.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        rows={rows}
        perPage={filters.perPage}
        loading={loading}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
      />
    </PlatformAdminLayout>
  );
};

export default PlatformAuditLog;
